<template>
  <div v-if="props.isOpen" class="fixed inset-0 z-[100] overflow-y-auto no-scrollbar" @click="closeModal">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-4 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity" aria-hidden="true">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full max-h-[calc(100vh-64px)] overflow-hidden" 
        @click.stop>
        <div class="flex flex-col bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[calc(100vh-64px)]">
          <div class="flex flex-none items-center justify-between mb-3">
            <div class="flex items-center gap-3">
              <Tooltip text="Editor Mode" position="right" v-if="props.showEditor">
                <button @click="editorPage()" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full p-1 cursor-pointer">
                  <BookOpenIcon class="w-6 h-6 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                </button>
              </Tooltip>
              
              <!-- Offline Switch -->
              <Tooltip :text="offlineCacheEnabled ? $t('menu.offlineAvailable') : $t('menu.offlineMode')" position="right">
                <label class="relative inline-flex items-center cursor-pointer select-none">
                  <input type="checkbox" v-model="offlineCacheEnabled" @change="onToggleOfflineCache" class="sr-only peer">
                  <div class="w-9 h-5 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                  <span class="ml-2 text-xs font-medium text-gray-500 dark:text-gray-400">Offline</span>
                </label>
              </Tooltip>
            </div>
            <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white truncate max-w-[200px] sm:max-w-xs">
              {{ playlist?.name || 'Playlist' }}
            </h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <!-- Offline Sync Status Banner -->
          <div v-if="offlineCacheEnabled" class="mb-3">
            <div v-if="isSyncing" class="flex items-center justify-between text-xs text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-100 dark:border-indigo-900/50 px-3 py-1.5 rounded-lg shadow-sm">
              <span class="flex items-center gap-1.5 font-medium">
                <ArrowPathIcon class="w-4 h-4 animate-spin text-indigo-600 dark:text-indigo-400" />
                {{ $t('menu.offlineSyncing', { current: syncProgress.current, total: syncProgress.total }) }}
              </span>
              <span class="font-bold">{{ Math.round((syncProgress.current / (syncProgress.total || 1)) * 100) }}%</span>
            </div>
            <div v-else-if="cachedSongIds.size > 0 && cachedSongIds.size >= (playlist?.songs?.length || 0)" class="flex items-center justify-between text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-900/50 px-3 py-1.5 rounded-lg shadow-sm">
              <span class="flex items-center gap-1.5 font-medium">
                <CheckCircleIcon class="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                {{ $t('menu.offlineReady') }}
              </span>
              <span class="font-bold text-[11px] bg-emerald-100 dark:bg-emerald-900/60 px-2 py-0.5 rounded-full">{{ cachedSongIds.size }}/{{ playlist?.songs?.length || 0 }}</span>
            </div>
            <div v-else-if="appStore.state.isOnline" class="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 px-3 py-1.5 rounded-lg">
              <span class="flex items-center gap-1.5">
                <CloudArrowDownIcon class="w-4 h-4 text-indigo-500" />
                <span>{{ cachedSongIds.size }}/{{ playlist?.songs?.length || 0 }} {{ $t('menu.offlineAvailable') }}</span>
              </span>
              <button @click="syncAllSongsOffline" class="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer">
                {{ $t('menu.syncOfflineNow') }}
              </button>
            </div>
          </div>
          
          <div class="flex-1 overflow-y-auto pr-4 pl-4 no-scrollbar relative min-h-[150px]">
            <ul class="space-y-1 max-h-full">
              <li v-for="(song, index) in playlist?.songs" :key="getSongId(song) || index" 
                  class="flex items-center p-2 rounded-lg transition-all duration-200 cursor-pointer"
                  :class="[
                    isSongLocked(index)
                      ? 'opacity-50 bg-gray-50/40 dark:bg-gray-800/30 hover:bg-gray-100/60 dark:hover:bg-gray-700/50' 
                      : (isCurrentSong(song)
                          ? 'active-song text-white bg-gradient-to-r from-indigo-500 to-purple-500 dark:bg-gradient-to-r dark:from-indigo-900 dark:to-purple-900 hover:from-indigo-600 hover:to-purple-600 dark:hover:from-indigo-800 dark:hover:to-purple-800'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700')
                  ]"
                  :style="isSongLocked(index) ? 'filter: blur(8px); transform: scale(1.02); user-select: none;' : ''"
                  @click="selectSong(song, index)">
                <div class="flex items-center w-full" :class="{'text-white': isCurrentSong(song) && !isSongLocked(index)}">
                  <div class="flex-shrink-0 w-8 text-center text-sm text-gray-500 dark:text-gray-400" :class="{'text-white': isCurrentSong(song) && !isSongLocked(index)}">
                    {{ index + 1 }}
                  </div>
                  <div class="flex items-center ml-3 flex-1 min-w-0">
                    <div class="flex-shrink-0" :class="{'text-white': isCurrentSong(song) && !isSongLocked(index)}">
                      <LockClosedIcon v-if="isSongLocked(index)" class="w-5 h-5 text-indigo-500 dark:text-indigo-400" />
                      <svg v-else-if="isSongItem(song)" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" 
                        :class="{'text-white': isCurrentSong(song)}"
                        class="w-5 h-5 text-gray-400">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m9 9 10.5-3m0 6.553v3.75a2.25 2.25 0 0 1-1.632 2.163l-1.32.377a1.803 1.803 0 1 1-.99-3.467l2.31-.66a2.25 2.25 0 0 0 1.632-2.163Zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 0 1-1.632 2.163l-1.32.377a1.803 1.803 0 1 1-.99-3.467l2.31-.66A2.25 2.25 0 0 0 9 15.553Z" />
                      </svg>
                      <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" 
                        :class="{'text-white': isCurrentSong(song)}"
                        class="w-5 h-5 text-gray-400" >
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                      </svg>
                    </div>
                    <div class="ml-3 flex-1 min-w-0">
                      <p class="text-sm font-medium text-gray-900 dark:text-white truncate" :class="{'text-white': isCurrentSong(song) && !isSongLocked(index)}">
                        {{ getSongTitle(song, index) }}
                      </p>
                      <p v-if="getSongArtist(song)" class="text-xs text-gray-500 dark:text-gray-400 truncate" :class="{'text-white': isCurrentSong(song) && !isSongLocked(index)}">
                        {{ getSongArtist(song) }}
                      </p>
                    </div>

                    <!-- Tags -->
                    <div v-if="getSongTags(song).length > 0" class="ml-2 flex-shrink-0">
                      <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                            :class="isCurrentSong(song) && !isSongLocked(index) ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'">
                        {{ typeof getSongTags(song)[0] === 'object' && getSongTags(song)[0] !== null ? getSongTags(song)[0].name : getSongTags(song)[0] }}
                      </span>
                    </div>

                    <!-- Offline Cache Indicator Icon -->
                    <div v-if="offlineCacheEnabled && !isSongLocked(index)" class="ml-2 flex-shrink-0">
                      <Tooltip :text="isSongCached(song) ? $t('menu.offlineAvailable') : $t('menu.offlineNotCached')" position="left">
                        <CheckCircleIcon v-if="isSongCached(song)" class="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                        <ArrowDownCircleIcon v-else class="w-4 h-4 text-gray-300 dark:text-gray-600 hover:text-indigo-500" @click.stop="downloadSingleSong(song)" />
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>

          <!-- Locked Content Overlay Pinned at Bottom of Modal -->
          <div v-if="isFreeUser && (playlist?.songs?.length || 0) > maxFreeSongs" 
               class="flex-none mt-3 rounded-xl bg-gradient-to-r from-indigo-50/90 via-purple-50/90 to-indigo-50/90 dark:from-gray-700/80 dark:via-gray-700/90 dark:to-gray-700/80 backdrop-blur-md p-3.5 flex items-center justify-between border border-indigo-100 dark:border-indigo-900/40 shadow-sm">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center flex-shrink-0">
                <LockClosedIcon class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div class="text-left">
                <h4 class="font-bold text-xs text-gray-900 dark:text-white uppercase tracking-wider">
                  {{ $t('lockedContent.title') || 'CONTEÚDO BLOQUEADO' }}
                </h4>
                <p class="text-[11px] text-gray-500 dark:text-gray-400">
                  {{ $t('lockedContent.modalDescription', { limit: maxFreeSongs }) || `Limite de ${maxFreeSongs} músicas para usuário gratuito.` }}
                </p>
              </div>
            </div>
            <button v-if="isSubscriptionEnabled()"
                    @click="openSubscribe" 
                    class="px-4 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-wider shadow-sm transition-transform active:scale-95 cursor-pointer flex-shrink-0">
              {{ $t('lockedContent.subscribeButton') || 'Assine' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { defineProps, defineEmits, computed, watch, nextTick, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import playlistStore from '@/store/playlist.store'
import userStore from '@/store/user.store'
import appStore from '@/store/app.store'
import playlistService from '@/service/playlist.service'
import dbService from '@/service/db.service'
import { openSubscriptionModal } from '@/store/app.events'
import { isSubscriptionEnabled } from '@/service/subscription.service'
import { BookOpenIcon, LockClosedIcon, CheckCircleIcon, ArrowDownCircleIcon, ArrowPathIcon, CloudArrowDownIcon } from '@heroicons/vue/24/outline'

const maxFreeSongs = 10
const isFreeUser = computed(() => {
  return userStore.getters.isFree;
})

const isSongLocked = (index) => {
  return isFreeUser.value && index >= maxFreeSongs
}

const offlineCacheEnabled = computed({
  get: () => appStore.state.offlineCacheEnabled,
  set: (val) => appStore.commit('SET_OFFLINE_CACHE_ENABLED', val)
})

const cachedSongIds = ref(new Set())
const isSyncing = ref(false)
const syncProgress = ref({ current: 0, total: 0 })

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  showEditor: {
    type: Boolean,
    default: false
  }
})

const getSongTags = (song) => {
    let metadataChanged = song.songMetadataChanged || song.song_metadata_changed;
    if (typeof metadataChanged === 'string') {
        try { 
            metadataChanged = JSON.parse(metadataChanged); 
        } catch (e) {
            console.warn("Failed to parse song metadata", e);
        }
    }
    
    let tagsToUse = [];
    if (metadataChanged && Array.isArray(metadataChanged.tags) && metadataChanged.tags.length > 0) {
        tagsToUse = metadataChanged.tags;
    } else {
        tagsToUse = song.data?.tags || song.tags || [];
    }
    
    return tagsToUse;
};

const emit = defineEmits(['close'])
const route = useRoute()
const router = useRouter()

const playlist = computed(() => playlistStore.state.playlist)
const currentSongId = computed(() => route.params.songId)

const closeModal = () => {
  emit('close')
}

const getSongId = (song) => {
  return song?.data?._id || song?.data?.id || song?.song_id || song?.songID || song?.song || song?.id || song?._id;
}

const isSongItem = (song) => {
  if (!song) return false;
  return song.type !== 'TEXT' && song.type !== 'ELEMENT';
}

const getSongTitle = (song, index) => {
  return song?.data?.title || song?.title || song?.name || ('Música ' + (index + 1));
}

const getSongArtist = (song) => {
  return song?.data?.artist?.name || song?.artist?.name || (typeof song?.artist === 'string' ? song.artist : '') || (typeof song?.data?.artist === 'string' ? song.data.artist : '');
}

const isCurrentSong = (song) => {
  const sId = getSongId(song);
  return !!sId && sId === currentSongId.value;
}

const isSongCached = (song) => {
  const sId = getSongId(song);
  return !!sId && cachedSongIds.value.has(sId);
}

const checkCachedSongs = async () => {
  if (!playlist.value?.songs) return;
  const pId = playlist.value._id || playlist.value.id || route.params.id;
  const newSet = new Set();

  for (const song of playlist.value.songs) {
    const sId = getSongId(song);
    if (!sId) continue;

    // Check in local cache memory/localStorage
    const localKey = playlistService.PLAYLIST_SONG_CACHE_KEY.replace('[id]', pId).replace('[songId]', sId);
    if (window.cacheInstance && window.cacheInstance.getItem(localKey)) {
      newSet.add(sId);
      continue;
    }

    // Check in CacheStorage (Service Worker)
    if (typeof caches !== 'undefined') {
      try {
        const cache = await caches.open('triad-content-cache');
        const match = await cache.match(`/api/playlist/${pId}/song/${sId}`, { ignoreSearch: true });
        if (match) {
          newSet.add(sId);
          continue;
        }
        const rawSongId = song.song_id || song.songID || song.song;
        if (rawSongId) {
          const rawMatch = await cache.match(`/api/song/${rawSongId}`, { ignoreSearch: true });
          if (rawMatch) {
            newSet.add(sId);
          }
        }
      } catch (e) {
        // Cache storage check ignored if unavailable
      }
    }
  }
  cachedSongIds.value = newSet;
}

const syncAllSongsOffline = async () => {
  if (!playlist.value?.songs || !appStore.state.isOnline || isSyncing.value) return;
  const pId = playlist.value._id || playlist.value.id || route.params.id;
  const songs = playlist.value.songs;
  
  isSyncing.value = true;
  syncProgress.value = { current: 0, total: songs.length };

  for (const song of songs) {
    const sId = getSongId(song);
    if (sId && pId) {
      try {
        await playlistService.fetchSong({ id: pId, songId: sId, force: true });
        
        const uId = userStore?.state?.userInfo?.id || 'anon';
        const notesKey = `notes-user-${uId}-playlist-${pId}-song-${sId}`;
        const drawingKey = `saved-drawing-user-${uId}-${pId}-${sId}`;

        // Prefetch notes into localStorage & IndexedDB
        fetch(`/api/playlist/${pId}/song/${sId}/notes`).then(r => r.json()).then(notes => {
          if (Array.isArray(notes)) {
            try { localStorage.setItem(notesKey, JSON.stringify(notes)); } catch (e) {}
            dbService.setItem(notesKey, notes);
          }
        }).catch(() => {});

        // Prefetch drawing into localStorage & IndexedDB
        fetch(`/api/playlist/${pId}/song/${sId}/drawing`).then(r => r.json()).then(res => {
          if (res && res.drawing) {
            try { localStorage.setItem(drawingKey, JSON.stringify(res.drawing)); } catch (e) {}
            dbService.setItem(drawingKey, res.drawing);
          }
        }).catch(() => {});

        cachedSongIds.value.add(sId);
        // Force reactivity update on Set
        cachedSongIds.value = new Set(cachedSongIds.value);
      } catch (e) {
        console.warn(`[SongListModal] Could not prefetch song ${sId}:`, e);
      }
    }
    syncProgress.value.current++;
  }

  isSyncing.value = false;
  await checkCachedSongs();
}

const downloadSingleSong = async (song) => {
  const pId = playlist.value?._id || playlist.value?.id || route.params.id;
  const sId = getSongId(song);
  if (!pId || !sId || !appStore.state.isOnline) return;

  try {
    await playlistService.fetchSong({ id: pId, songId: sId, force: true });
    const uId = userStore?.state?.userInfo?.id || 'anon';
    const notesKey = `notes-user-${uId}-playlist-${pId}-song-${sId}`;
    const drawingKey = `saved-drawing-user-${uId}-${pId}-${sId}`;

    fetch(`/api/playlist/${pId}/song/${sId}/notes`).then(r => r.json()).then(notes => {
      if (Array.isArray(notes)) {
        try { localStorage.setItem(notesKey, JSON.stringify(notes)); } catch (e) {}
        dbService.setItem(notesKey, notes);
      }
    }).catch(() => {});
    fetch(`/api/playlist/${pId}/song/${sId}/drawing`).then(r => r.json()).then(res => {
      if (res && res.drawing) {
        try { localStorage.setItem(drawingKey, JSON.stringify(res.drawing)); } catch (e) {}
        dbService.setItem(drawingKey, res.drawing);
      }
    }).catch(() => {});
    cachedSongIds.value.add(sId);
    cachedSongIds.value = new Set(cachedSongIds.value);
  } catch (e) {
    console.warn(`[SongListModal] Error downloading single song ${sId}:`, e);
  }
}

const onToggleOfflineCache = () => {
  if (offlineCacheEnabled.value && appStore.state.isOnline) {
    syncAllSongsOffline();
  }
}

const selectSong = (song, index) => {
  if (isSongLocked(index)) {
    openSubscribe();
    return;
  }
  const songId = getSongId(song);
  const playlistId = playlist.value?._id || playlist.value?.id || route.params.id;
  if (!songId || !playlistId) {
    console.warn("Could not determine songId or playlistId", song, playlist.value);
    closeModal();
    return;
  }
  router.push({
    name: 'Playlist Songs', 
    params: { 
      id: playlistId, 
      songId: songId 
    }
  });
  closeModal();
}

const editorPage = () => {
  const playlistId = playlist.value?._id || playlist.value?.id || route.params.id;
  router.push({ path: '/playlist/' + playlistId + '/editor' })
}

const openSubscribe = () => {
  closeModal()
  openSubscriptionModal()
}

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    const pId = playlist.value?._id || playlist.value?.id || route.params.id;
    if (pId) {
      // Re-fetch playlist to guarantee latest song order and titles in modal
      playlistStore.dispatch("load", { id: pId, force: true });
    }
    checkCachedSongs();
    if (offlineCacheEnabled.value && appStore.state.isOnline && cachedSongIds.value.size < (playlist.value?.songs?.length || 0)) {
      syncAllSongsOffline();
    }
    nextTick(() => {
      const activeSong = document.querySelector('.active-song')
      if (activeSong) {
        activeSong.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    })
  }
})
</script> 